import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, ArrowLeft, SkipForward, Check, Sparkles, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { FieldHint } from "@/components/FieldHint";

const OFFER_TYPES = ["course", "coaching", "template", "toolkit", "service", "workshop", "other"];

const QUESTIONS = [
  { key: "selling", q: "What idea do you want help figuring out?", help: "It doesn't need to be fully formed. Share what you have, and MILO will help you work out what comes next.", hint: "This is your offer — the product or service people pay you for. Describe it in plain words, like you'd tell a friend.", ph: "e.g. A 7-day course that teaches busy parents to cook healthy dinners fast" },
  { key: "audience", q: "Who is it for?", help: "Describe the people you're trying to reach.", hint: "Your target audience: the specific group you're creating for. Specific beats broad — 'busy new parents' not 'everyone'.", ph: "e.g. Working parents with young kids and no time to cook" },
  { key: "problem", q: "What would make the biggest difference to them?", help: "Describe the result they want most and how achieving it would improve their life/business.", hint: "The frustration or struggle your audience deals with right now, before they find you. This is the pain your offer removes.", ph: "e.g. They're tired, short on time, and keep ordering takeout" },
  { key: "result", q: "What result do they get after finishing?", help: "How life looks better once they've used your offer.", hint: "The 'after' — how their life or situation improves once they've used your offer. Paint the win they're paying for.", ph: "e.g. They cook tasty, healthy dinners in under 20 minutes without stress" },
  { key: "tried", q: "What have they already tried that didn't work?", help: "This is optional — skip if you're not sure.", hint: "Things they've already attempted that didn't fully work. Naming these makes your pitch feel understanding. Optional.", ph: "e.g. Meal-kit boxes, random recipe videos, strict diets", optional: true },
];

const BLANK = { name: "", selling: "", audience: "", problem: "", result: "", tried: "", offer_type: "course" };

export default function OfferProfileBuilder() {
  const navigate = useNavigate();
  const { pid } = useParams();
  const location = useLocation();
  const { setHasProfile, refreshProfile, refreshCredits } = useAuth();

  const isOnboarding = location.pathname === "/onboarding";
  const isEdit = !!pid;

  const [step, setStep] = useState(0);
  const total = QUESTIONS.length + 1; // text questions + offer type/name step
  const [answers, setAnswers] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!isEdit);
  const [suggestions, setSuggestions] = useState([]);
  const [suggesting, setSuggesting] = useState(false);

  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/offer-profiles/${pid}`)
      .then((r) => setAnswers({ ...BLANK, ...r.data }))
      .catch(() => { toast.error("Could not load that profile"); navigate("/offer-profiles"); })
      .finally(() => setLoaded(true));
  }, [pid, isEdit, navigate]);

  // Pre-fill from the public Idea Generator lead magnet (if the user came from there).
  useEffect(() => {
    if (isEdit) return;
    try {
      const raw = localStorage.getItem("h2i_idea_prefill");
      if (!raw) return;
      const { answers: a } = JSON.parse(raw);
      if (a) {
        setAnswers((prev) => ({
          ...prev,
          selling: a.topic || prev.selling,
          audience: a.audience || prev.audience,
          problem: a.problem || prev.problem,
          result: a.result || prev.result,
          tried: a.tried && a.tried.toLowerCase() === "yes" ? "" : prev.tried,
        }));
        toast.success("We pre-filled your answers from the Idea Generator — tweak anything you like.");
      }
      localStorage.removeItem("h2i_idea_prefill");
    } catch (e) { console.warn("Could not read Idea Generator prefill from localStorage:", e); }
  }, [isEdit]);

  const onText = step < QUESTIONS.length;
  const cur = QUESTIONS[step];
  const canNext = onText ? (cur.optional || answers[cur.key].trim().length > 0) : true;

  const getSuggestions = async () => {
    setSuggesting(true);
    setSuggestions([]);
    try {
      const { data } = await api.post("/offer-profiles/suggest", { field: cur.key, answers });
      setSuggestions(data.suggestions || []);
      refreshCredits();
    } catch (e) {
      if (e.response?.status !== 402) toast.error("Couldn't get suggestions right now. Try again.");
    } finally {
      setSuggesting(false);
    }
  };

  const applySuggestion = (s) => { set(cur.key, s); setSuggestions([]); };

  const goStep = (n) => { setSuggestions([]); setStep(n); };

  const submit = async () => {
    setSaving(true);
    const payload = {
      name: answers.name, selling: answers.selling, audience: answers.audience,
      problem: answers.problem, result: answers.result, tried: answers.tried,
      offer_type: answers.offer_type,
    };
    try {
      if (isEdit) {
        await api.put(`/offer-profiles/${pid}`, payload);
        await refreshProfile();
        toast.success("Profile updated — re-run the Pitch Test before building");
        navigate(`/pitch-test/${pid}`);
      } else {
        const { data } = await api.post("/offer-profiles", payload);
        setHasProfile(true);
        await refreshProfile();
        toast.success("Offer Profile saved! Let's pitch-test it.");
        navigate(`/pitch-test/${data.id}`);
      }
    } catch (e) {
      toast.error("Could not save. Please try again.");
      setSaving(false);
    }
  };

  const next = () => { if (step < total - 1) goStep(step + 1); else submit(); };

  const title = isEdit ? "Edit Offer Profile" : isOnboarding ? "Set up your Offer Profile" : "New Offer Profile";

  return (
    <div className={isOnboarding ? "flex min-h-screen items-center justify-center bg-background px-4 py-10" : ""}>
      <div className="w-full max-w-xl fade-up" data-testid="offer-profile-builder">
        {isOnboarding && (
          <div className="mb-6 flex items-center gap-2.5">
            <img src="/logo-mark.png" alt="Hook2Income AI" className="h-9 w-9 object-contain" />
            <span className="font-head text-lg font-bold">
              Hook2Income<span style={{ color: "hsl(var(--gold))" }}> AI</span>
            </span>
          </div>
        )}
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
          {title} · {step + 1} of {total}
        </p>
        <Progress value={((step + 1) / total) * 100} className="mt-4 h-2" />

        {loaded && (() => {
          const answered = QUESTIONS
            .map((q, i) => ({ ...q, i, val: (answers[q.key] || "").trim() }))
            .filter((x) => x.val && x.i !== step);
          if (answered.length === 0) return null;
          return (
            <div className="mt-4 rounded-2xl border border-border bg-card/60 p-3.5" data-testid="builder-progress-summary">
              <p className="mb-2 text-xs font-semibold text-muted-foreground">Your answers so far — tap the pencil to edit any</p>
              <div className="flex flex-col gap-1.5">
                {answered.map((x) => (
                  <div key={x.key} data-testid={`builder-answer-${x.key}`}
                    className="flex items-start justify-between gap-2 rounded-lg bg-secondary/40 px-3 py-1.5 text-xs">
                    <span className="min-w-0">
                      <span className="font-semibold text-foreground">{x.q}</span>{" "}
                      <span className="text-muted-foreground">{x.val.length > 80 ? `${x.val.slice(0, 80)}…` : x.val}</span>
                    </span>
                    <button type="button" onClick={() => goStep(x.i)} data-testid={`builder-edit-${x.key}`}
                      title={`Edit — ${x.q}`}
                      className="shrink-0 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary">
                      <Pencil size={13} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {!loaded ? (
          <p className="mt-8 text-muted-foreground">Loading...</p>
        ) : (
        <div className="mt-8">
          {onText ? (
            <div className="space-y-3">
              <h1 className="font-head text-3xl font-bold" data-testid="builder-question">
                {cur.q}
                <FieldHint testid={`hint-builder-${cur.key}`} text={cur.hint} />
              </h1>
              <p className="text-sm text-muted-foreground">{cur.help}</p>
              <Textarea
                autoFocus
                data-testid={`builder-input-${cur.key}`}
                value={answers[cur.key]}
                onChange={(e) => set(cur.key, e.target.value)}
                rows={4}
                placeholder={cur.ph}
                className="mt-2 text-base"
              />

              <div>
                <Button variant="outline" size="sm" onClick={getSuggestions} disabled={suggesting}
                  data-testid="builder-suggest-btn"
                  className="rounded-full border-primary/40 text-primary hover:bg-primary/5">
                  {suggesting ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Sparkles size={15} className="mr-1.5" />}
                  {suggesting ? "Thinking..." : "Suggest ideas"}
                </Button>
                <span className="ml-2 text-xs text-muted-foreground">Costs 5 credits</span>
              </div>

              {suggestions.length > 0 && (
                <div className="space-y-2 pt-1" data-testid="builder-suggestions">
                  {suggestions.map((s, i) => (
                    <button key={s || i} type="button" onClick={() => applySuggestion(s)}
                      data-testid={`builder-suggestion-${i}`}
                      className="block w-full rounded-xl border border-border bg-secondary/40 p-3 text-left text-sm transition-colors hover:border-primary/50 hover:bg-primary/5">
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              <div className="space-y-3">
                <h1 className="font-head text-3xl font-bold">
                  What type of offer is it?
                  <FieldHint testid="hint-builder-offer-type" text="The format of your offer — a course, coaching, template, toolkit, and so on. Pick the closest fit; it shapes how we package everything." />
                </h1>
                <p className="text-sm text-muted-foreground">Pick the one that fits best.</p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {OFFER_TYPES.map((t) => {
                    const active = answers.offer_type === t;
                    return (
                      <button key={t} type="button" onClick={() => set("offer_type", t)}
                        data-testid={`builder-type-${t}`}
                        className={`relative rounded-xl border p-4 text-sm font-semibold capitalize transition-transform duration-200 hover:-translate-y-0.5 ${
                          active ? "border-primary bg-primary/5 ring-2 ring-primary" : "border-border hover:border-primary/40"
                        }`}>
                        {active && <Check size={14} className="absolute right-2 top-2 text-primary" />}
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-semibold">
                  Name this profile
                  <FieldHint testid="hint-builder-name" text="A private label just for you, so you can find this profile again later. Your customers never see it." />
                </p>
                <p className="text-sm text-muted-foreground">So you can find it later (one per offer or niche).</p>
                <Input data-testid="builder-name" value={answers.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder={answers.selling ? answers.selling.slice(0, 40) : "e.g. Fast Family Dinners"}
                  className="text-base" />
              </div>
            </div>
          )}
        </div>
        )}

        <div className="mt-8 flex items-center justify-between">
          <Button variant="ghost"
            onClick={() => (step === 0 ? navigate(isOnboarding ? -1 : "/offer-profiles") : goStep(step - 1))}
            disabled={step === 0 && isOnboarding}
            data-testid="builder-back">
            <ArrowLeft size={18} className="mr-1" /> {step === 0 && !isOnboarding ? "Cancel" : "Back"}
          </Button>
          <div className="flex gap-2">
            {onText && cur.optional && (
              <Button variant="outline" onClick={() => { set(cur.key, ""); next(); }} data-testid="builder-skip" className="rounded-full">
                <SkipForward size={16} className="mr-1" /> Skip
              </Button>
            )}
            <Button onClick={next} disabled={!canNext || saving} data-testid="builder-next"
              className="rounded-full bg-primary px-6 hover:bg-primary/90">
              {step < total - 1 ? (<>Continue <ArrowRight size={18} className="ml-1" /></>)
                : (saving ? "Saving..." : isEdit ? "Save changes" : "Finish")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
